import { DOCUMENT } from '@angular/common';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, take } from 'rxjs';

/**
 *
 * When a dialog is opened from a `<mat-menu>` item, the item is destroyed as
 * soon as the menu closes. Material's automatic `restoreFocus` then tries to
 * focus a node that no longer exists in the DOM and silently falls back to
 * `<body>` — the user loses focus and has to Tab again from the top.
 *
 * This service bridges the gap:
 *  - Globally tracks the last focused element that is NOT inside a
 *    `.mat-mdc-menu-panel` (menu items are excluded because they vanish).
 *  - On each `MatDialog.afterOpened`, snapshots that element as the "trigger".
 *  - On `beforeClosed`, focuses the snapshotted trigger via a microtask so it
 *    runs after Material's own restore attempt.
 *
 * Result: opening a dialog from a `<button>`, a `<mat-list-item>`, a custom
 * `<div role="button">` or a `<button mat-menu-item>` all behave the same —
 * focus comes back to the originating element on dialog close.
 *
 * Stacked dialogs work too: each dialog keeps its own trigger snapshot in a
 * closure, so closing dialog B returns focus to the button in dialog A, and
 * closing A returns focus to the original page trigger.
 *
 * Coexists with Material's `restoreFocus: true` default — Material runs first
 * (covers most cases without ever firing this service), this fires last as a
 * safety net for the destroyed-trigger case.
 *
 */
@Injectable({ providedIn: 'root' })
export class DialogFocusRestoreService implements OnDestroy {
  private readonly dialog = inject(MatDialog);
  private readonly doc = inject(DOCUMENT);

  private lastNonMenuFocus: HTMLElement | null = null;
  private subscriptions: Subscription[] = [];

  init(): void {
    if (this.subscriptions.length) return;
    this.doc.addEventListener('focusin', this.onFocusIn, true);
    this.subscriptions.push(
      this.dialog.afterOpened.subscribe(ref => {
        // Snapshot the trigger at the moment the dialog opens. Closure keeps it
        // alive across nested dialogs.
        const trigger = this.lastNonMenuFocus;
        ref
          .beforeClosed()
          .pipe(take(1))
          .subscribe(() => {
            if (!trigger?.isConnected) return;
            // Microtask: run after Material's own restoreFocus.
            queueMicrotask(() => trigger.focus());
          });
      }),
    );
  }

  ngOnDestroy(): void {
    this.doc.removeEventListener('focusin', this.onFocusIn, true);
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
    this.lastNonMenuFocus = null;
  }

  private onFocusIn = (event: Event) => {
    const target = event.target as HTMLElement;
    if (!target || target === this.doc.body) return;
    // Mat-menu items disappear when the menu closes — never store one as the
    // restore target. Buttons inside dialogs and other overlays are fine and
    // become the trigger for any further dialog that opens from inside them.
    if (target.closest('.mat-mdc-menu-panel')) return;
    this.lastNonMenuFocus = target;
  };
}
